import { useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  DataTable,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  RowActions,
  StatusBadge,
  Switch,
  toast,
  type DataTableColumn,
} from '@/components/ui';
import { Check, X } from 'lucide-react';
import type { GradeItem } from '../hooks/useUserGradesQuery';
import { GradeDiscountExclusionsDialog, exclusionSummary } from './GradeDiscountExclusionsDialog';
import {
  useUserGradeDeleteMutation,
  useUserGradePatchMutation,
} from '../hooks/useUserGradeMutation';

interface Props {
  items: ReadonlyArray<GradeItem>;
}

interface EditState {
  gradeId: string;
  gradeName: string;
  priority: string;
  discountRate: string;
  mileageRate: string;
  allowBankTransfer: boolean;
}

export const GradeTable = ({ items }: Props) => {
  const { t } = useTranslation('common');
  const patchMutation = useUserGradePatchMutation();
  const deleteMutation = useUserGradeDeleteMutation();
  const [editing, setEditing] = useState<EditState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<GradeItem | null>(null);
  const [exclusionTarget, setExclusionTarget] = useState<GradeItem | null>(null);

  const commitEdit = () => {
    if (!editing) return;
    const trimmed = editing.gradeName.trim();
    const prio = Number(editing.priority);
    // 할인율/추가 적립률: 빈 값=미변경, 입력 시 0~100 범위 내에서만 패치(범위 밖이면 생략하여 기존값 유지).
    const rateOf = (raw: string): number | undefined => {
      const v = Number(raw.trim());
      return raw.trim() !== '' && Number.isFinite(v) && v >= 0 && v <= 100 ? v : undefined;
    };
    const discountRate = rateOf(editing.discountRate);
    const mileageRate = rateOf(editing.mileageRate);
    patchMutation.mutate(
      {
        gradeId: editing.gradeId,
        ...(trimmed ? { gradeName: trimmed } : {}),
        ...(prio > 0 ? { priority: prio } : {}),
        ...(discountRate !== undefined ? { discountRate } : {}),
        ...(mileageRate !== undefined ? { mileageRate } : {}),
        // 무통장입금 허용은 항상 명시 전송(boolean 은 미변경 개념이 없어 현재 토글값을 반영).
        allowBankTransfer: editing.allowBankTransfer,
      },
      {
        onSuccess: () => {
          setEditing(null);
          toast.success(t('userGrades.toastSaved', '회원 등급이 수정되었습니다'));
        },
      },
    );
  };

  const columns: ReadonlyArray<DataTableColumn<GradeItem>> = [
    {
      key: 'gradeCode',
      header: t('userGrades.cols.gradeCode'),
      cell: (row) => (
        <span className="font-mono text-[12px]" style={{ color: 'var(--content-muted)' }}>
          {row.gradeCode}
        </span>
      ),
      width: 120,
    },
    {
      key: 'gradeName',
      header: t('userGrades.cols.gradeName'),
      cell: (row) =>
        editing?.gradeId === row.gradeId ? (
          <Input
            id={`grade-name-${row.gradeId}`}
            value={editing.gradeName}
            onChange={(e) =>
              setEditing((prev) => (prev ? { ...prev, gradeName: e.target.value } : null))
            }
            className="h-7 w-40 text-xs"
            maxLength={100}
            aria-label={t('userGrades.editGradeName')}
          />
        ) : (
          <span className="font-medium" style={{ color: 'var(--content-strong)' }}>
            {row.gradeName}
          </span>
        ),
    },
    {
      key: 'priority',
      header: t('userGrades.cols.priority'),
      cell: (row) =>
        editing?.gradeId === row.gradeId ? (
          <Input
            id={`grade-priority-${row.gradeId}`}
            type="number"
            min={1}
            max={999}
            value={editing.priority}
            onChange={(e) =>
              setEditing((prev) => (prev ? { ...prev, priority: e.target.value } : null))
            }
            className="h-7 w-20 text-xs"
            aria-label={t('userGrades.editPriority')}
          />
        ) : (
          <span className="tabular-nums">{row.priority}</span>
        ),
      width: 96,
    },
    {
      key: 'discountRate',
      header: t('userGrades.cols.discountRate', '할인율'),
      cell: (row) =>
        editing?.gradeId === row.gradeId ? (
          <Input
            id={`grade-discount-${row.gradeId}`}
            type="number"
            min={0}
            max={100}
            step="0.01"
            value={editing.discountRate}
            onChange={(e) =>
              setEditing((prev) => (prev ? { ...prev, discountRate: e.target.value } : null))
            }
            className="h-7 w-20 text-xs"
            aria-label={t('userGrades.editDiscountRate', '할인율 수정')}
          />
        ) : (
          <span className="tabular-nums">
            {row.discountRate.toLocaleString(undefined, { maximumFractionDigits: 2 })}%
          </span>
        ),
      width: 96,
      align: 'right',
    },
    {
      key: 'mileageRate',
      header: t('userGrades.cols.mileageRate', '추가 적립률'),
      cell: (row) =>
        editing?.gradeId === row.gradeId ? (
          <Input
            id={`grade-mileage-${row.gradeId}`}
            type="number"
            min={0}
            max={100}
            step="0.01"
            value={editing.mileageRate}
            onChange={(e) =>
              setEditing((prev) => (prev ? { ...prev, mileageRate: e.target.value } : null))
            }
            className="h-7 w-20 text-xs"
            aria-label={t('userGrades.editMileageRate', '추가 적립률 수정')}
          />
        ) : (
          <span className="tabular-nums">
            {row.mileageRate.toLocaleString(undefined, { maximumFractionDigits: 2 })}%
          </span>
        ),
      width: 100,
      align: 'right',
    },
    {
      key: 'allowBankTransfer',
      header: t('userGrades.cols.allowBankTransfer', '무통장입금'),
      cell: (row) =>
        editing?.gradeId === row.gradeId ? (
          <div className="flex justify-center">
            <Switch
              id={`grade-bank-${row.gradeId}`}
              checked={editing.allowBankTransfer}
              onCheckedChange={(v) =>
                setEditing((prev) => (prev ? { ...prev, allowBankTransfer: v } : null))
              }
              aria-label={t('userGrades.editAllowBankTransfer', '무통장입금 허용 수정')}
            />
          </div>
        ) : row.allowBankTransfer ? (
          <StatusBadge label={t('userGrades.bankTransfer.allowed', '허용')} variant="success" />
        ) : (
          <StatusBadge label={t('userGrades.bankTransfer.blocked', '차단')} variant="neutral" />
        ),
      width: 96,
      align: 'center',
    },
    {
      key: 'memberCount',
      header: t('userGrades.cols.memberCount'),
      cell: (row) => (
        <span className="tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.memberCount.toLocaleString()}
          {t('userGrades.memberCountUnit')}
        </span>
      ),
      width: 100,
      align: 'right',
    },
    {
      // QA-464 — 등급 할인 제외(공급사·카테고리·브랜드·상품). 응답에 필드가 없으면(BE 반영 전) "—" 이고 설정을 막는다.
      key: 'discountExclusions',
      header: t('userGrades.cols.discountExclusions', '할인 제외'),
      cell: (row) => (
        <div className="flex items-center gap-2">
          <span className="text-[12px]" style={{ color: 'var(--content-muted)' }} data-testid={`grade-exclusion-summary-${row.gradeId}`}>
            {row.discountExclusions ? exclusionSummary(row.discountExclusions) : '—'}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={!row.discountExclusions}
            title={row.discountExclusions ? undefined : t('userGrades.exclusions.notReady', '서버(BE) 반영 후 설정할 수 있습니다.')}
            onClick={() => setExclusionTarget(row)}
            data-testid={`grade-exclusion-edit-${row.gradeId}`}
          >
            {t('userGrades.exclusions.edit', '설정')}
          </Button>
        </div>
      ),
    },
    {
      key: 'active',
      header: t('userGrades.cols.active'),
      cell: (row) =>
        row.active ? (
          <StatusBadge label={t('userGrades.active')} variant="success" />
        ) : (
          <StatusBadge label={t('userGrades.inactive')} variant="neutral" />
        ),
      width: 80,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: 96,
      cell: (row) => {
        if (editing?.gradeId === row.gradeId) {
          return (
            <div className="flex items-center justify-end gap-1">
              <Button
                variant="ghost"
                size="sm"
                aria-label={t('userGrades.actions.save', '저장')}
                disabled={patchMutation.isPending}
                onClick={commitEdit}
              >
                <Check size={14} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                aria-label={t('userGrades.cancel', '취소')}
                onClick={() => setEditing(null)}
              >
                <X size={14} />
              </Button>
            </div>
          );
        }
        return (
          <RowActions
            triggerLabel={`${row.gradeName} ${t('userGrades.actions.menu', '액션')}`}
            actions={[
              {
                key: 'edit',
                label: t('userGrades.editAria', { gradeName: row.gradeName }),
                onClick: () =>
                  setEditing({
                    gradeId: row.gradeId,
                    gradeName: row.gradeName,
                    priority: String(row.priority),
                    discountRate: String(row.discountRate),
                    mileageRate: String(row.mileageRate),
                    allowBankTransfer: row.allowBankTransfer,
                  }),
              },
              {
                key: 'toggle',
                label: row.active ? t('userGrades.deactivate') : t('userGrades.activate'),
                disabled: patchMutation.isPending,
                onClick: () =>
                  patchMutation.mutate({ gradeId: row.gradeId, active: !row.active }),
              },
              {
                key: 'delete',
                label: t('userGrades.deleteAria', { gradeName: row.gradeName }),
                variant: 'danger',
                separatorBefore: true,
                disabled: deleteMutation.isPending,
                onClick: () => setDeleteTarget(row),
              },
            ]}
          />
        );
      },
    },
  ];

  return (
    <>
      <DataTable
        columns={[...columns]}
        rows={[...items]}
        rowKey={(row) => row.gradeId}
        stickyHeader
        mobileCardCollapse
        emptyState={t('userGrades.empty')}
        ariaLabel={t('userGrades.title')}
      />

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('userGrades.deleteTitle')}</DialogTitle>
            <DialogDescription>
              {t('userGrades.confirmDelete', { gradeName: deleteTarget?.gradeName ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">
                {t('userGrades.cancel')}
              </Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (deleteTarget) {
                  deleteMutation.mutate(deleteTarget.gradeId, {
                    onSuccess: () => setDeleteTarget(null),
                  });
                }
              }}
            >
              {t('cms.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {exclusionTarget ? (
        <GradeDiscountExclusionsDialog grade={exclusionTarget} onClose={() => setExclusionTarget(null)} />
      ) : null}
    </>
  );
};
